import React from 'react';
import styled from '@emotion/styled';
import Link from 'ech-components/src/primitives/Link';
import { LinkVariant } from 'ech-components/src/primitives/Link/Link';
import { rem } from 'polished';
import { css } from '@emotion/react';
import { colors, spaces } from 'ech-components/src/core/styles/theme/default';
import { createRoutingURI, getRootSlug } from '@scripts/utils/domain';
import { useIntl } from 'react-intl';

const StyledInfoTag = styled.span`
  font-size: 9px;
  font-weight: 100;
  color: white;
  background: rgba(0, 0, 0, 0.3);
  padding: 2px 4px;
  letter-spacing: 0.05rem;
`;

const baseList = css`
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: ${rem(18)};
  line-height: ${rem(30)};
  font-weight: 500;
`;

const StyledUnorderedList = styled.ul`
  ${baseList};

  li {
    position: relative;
    margin-bottom: ${rem(spaces.epsilon)};
    padding-left: 17px;

    &::before {
      position: absolute;
      left: 0;
      top: 0;
      content: '•';
      color: ${colors.blue800};
    }
  }
`;

const StyledOrderedList = styled.ol`
  ${baseList};
  counter-reset: counter;

  li {
    counter-increment: counter;
    display: grid;
    grid-template-columns: auto 1fr;
    margin-bottom: ${rem(spaces.epsilon)};

    &::before {
      content: counter(counter);
      padding-right: ${rem(spaces.alpha)};
    }
  }
`;

const StyledLink = styled(Link)(
  ({ variant }: { variant: LinkVariant }) => css`
    position: relative;
    font-size: inherit;
    font-weight: 400;
    line-height: inherit;

    ${(variant === 'external' || variant === 'internal') &&
    css`
      svg {
        vertical-align: text-top;
        margin-left: ${rem(3)};
        margin-right: ${rem(3)};
      }
    `};

    &:hover .label {
      text-decoration: underline;
    }

    &:hover .label::after,
    &:focus .label::after {
      content: none;
    }
  `,
);

const CustomInline = ({ nodeType, data }: CustomInlineProps) => {
  const intl = useIntl();

  switch (nodeType) {
    case 'ContentfulExternalLink':
      return (
        <StyledLink
          variant="external"
          linkText={data.hyperlinkText || data.linkText}
          link={{
            href: data.url,
            target: '_blank',
            rel: 'noreferrer noopener',
          }}
          style={{ display: 'inline-block' }}
        />
      );
    case 'ContentfulInternalLink':
      return (
        <StyledLink
          variant="internal"
          linkText={data.hyperlinkText || data.linkText}
          link={{
            href: data.url,
          }}
          style={{ display: 'inline-block' }}
        />
      );
    case 'ContentfulMailLink':
      return (
        <StyledLink
          variant="internal"
          linkText={data.hyperlinkText || data.text}
          link={{
            href: `mailto:${data.emailAddress}`,
            target: '_blank',
            rel: 'noreferrer noopener',
          }}
          style={{ display: 'inline-block' }}
        />
      );
    case 'ContentfulUnorderedList':
      return <StyledUnorderedList />;
    case 'ContentfulOrderedList':
      return <StyledOrderedList />;
    case 'ContentfulDefaultHub':
    case 'ContentfulNewsHub':
    case 'ContentfulToolHub':
    case 'ContentfulStoryHub':
    case 'ContentfulEventHub':
    case 'ContentfulFundingHub':
      return (
        <StyledLink
          variant="internal"
          linkText={data.hyperlinkText || data.navigationTitle}
          link={{
            href: createRoutingURI(getRootSlug(data, intl.locale)),
          }}
          style={{ display: 'inline-block' }}
        />
      );
    case 'ContentfulCampaignConfiguration':
      return (
        <StyledLink
          variant="internal"
          linkText={data.hyperlinkText || data.campaignName}
          link={{
            href: createRoutingURI(getRootSlug(data, intl.locale)),
          }}
          style={{ display: 'inline-block' }}
        />
      );
    case 'ContentfulAsset':
      return (
        <StyledLink
          variant="download"
          linkText={data.hyperlinkText || data.title}
          link={{
            href: data.file.url,
            target: '_blank',
            rel: 'noreferrer noopener',
          }}
          style={{ display: 'inline-block' }}
        />
      );
    default:
      return <StyledInfoTag>(...)</StyledInfoTag>;
  }
};

export type CustomInlineProps = {
  /** Contentful node type */
  nodeType: string;
  /** Dataset for custom component */
  data: any;
};

export default CustomInline;
